import React, { useMemo, useState } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import DemoPermissionsDisplay from '../components/DemoPermissionsDisplay'

const DashboardPage: React.FC = () => {
  const dashboardAccess = useQuery(api.users.getDashboardAccess)
  const currentUser = useQuery(api.users.getUserInfo)
  const [requesterId, setRequesterId] = useState<string | null>(null)

  // Debug logging
  React.useEffect(() => {
    console.log('DashboardPage: currentUser =', currentUser)
  }, [currentUser])

  React.useEffect(() => {
    if (currentUser && (currentUser as any)._id) {
      setRequesterId((currentUser as any)._id)
    }
  }, [currentUser])

  const dashboardStats = useQuery(api.users.getDashboardStats as any, requesterId ? { userId: requesterId } : 'skip')
  const { totalClaims, pendingClaims, approvedClaims, deniedClaims } = useMemo(() => {
    if (dashboardStats && dashboardStats.hasAccess) {
      return {
        totalClaims: dashboardStats.totalClaims,
        pendingClaims: dashboardStats.pendingClaims,
        approvedClaims: dashboardStats.approvedClaims,
        deniedClaims: dashboardStats.deniedClaims
      };
    }
    return { totalClaims: 0, pendingClaims: 0, approvedClaims: 0, deniedClaims: 0 };
  }, [dashboardStats])

  const runSeeding = async () => {
    setSeeding(true)
    setSeedResult("")
    setSeedError("")
    try {
      // Start from a clean state
      try { await clearAll({}) } catch {}
      const baseRes: any = await seedBase({})
      const pas = baseRes?.pas || {}
      setSeedResult(`Seeded. Clients=${pas.clients ?? 0}, Policies=${pas.policies ?? 0}, Created=${pas.created ?? 0}, Updated=${pas.updated ?? 0}`)
    } catch (e: any) {
      setSeedError(e?.message || 'Failed to seed demo data')
    } finally {
      setSeeding(false)
    }
  }

  const runClearAll = async () => {
    setClearing(true)
    setClearResult("")
    setSeedError("")
    try {
      const res = await clearAll({}) as any
      setClearResult(`Cleared: policies=${res?.policies}, parties=${res?.parties}, claims=${res?.claims}, documents=${res?.documents}, auditLogs=${res?.auditLogs}, queue=${res?.requestQueue}`)
    } catch (e: any) {
      setSeedError(e?.message || 'Failed to clear data')
    } finally {
      setClearing(false)
    }
  }
  
  // Derived stats are calculated above via useMemo

  // Show loading state while dashboard access is loading
  if (dashboardAccess === undefined) {
    return (
      <div className="dashboard-page">
        <div className="page-header">
          <h1>Dashboard</h1>
          <p>Loading...</p>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading dashboard...</p>
        </div>
      </div>
    )
  }

  // Show access denied with role switching option
  if (!dashboardAccess.hasAccess) {
    return (
      <div className="dashboard-page">
        <div className="page-header">
          <h1>Dashboard</h1>
          <p>Access Issue</p>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center', background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '8px', margin: '2rem' }}>
          <h3>Dashboard Access</h3>
          <p>{dashboardAccess.error}</p>
          {dashboardAccess.canSwitchRoles && (
            <div style={{ marginTop: '1rem' }}>
              <p>You can switch to a different role to access the dashboard:</p>
              <button 
                onClick={() => window.location.reload()} 
                style={{ 
                  marginTop: '1rem', 
                  padding: '0.5rem 1rem', 
                  background: '#28a745', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px',
                  marginRight: '1rem'
                }}
              >
                Reload Page
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Welcome back, {currentUser?.name || 'User'}</p>
      </div>

      {/* Demo Permissions Display */}
      <DemoPermissionsDisplay />

      <div className="dashboard-grid">
        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <div className="stat-number" data-testid="stat-total-claims">{totalClaims}</div>
            <div className="stat-label">Total Claims</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <div className="stat-number" data-testid="stat-pending-claims">{pendingClaims}</div>
            <div className="stat-label">Pending Claims</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-number" data-testid="stat-approved-claims">{approvedClaims}</div>
            <div className="stat-label">Approved Claims</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">❌</div>
          <div className="stat-content">
            <div className="stat-number" data-testid="stat-denied-claims">{deniedClaims}</div>
            <div className="stat-label">Denied Claims</div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="recent-activity">
          <h2>Recent Activity</h2>
          <div className="activity-placeholder">
            <p>Recent claims activity will appear here once claims management is implemented.</p>
          </div>
        </div>

        <div className="quick-actions">
          <h2>Quick Actions</h2>
          <div className="action-buttons">
            <button className="action-button" disabled>
              <span className="button-icon">➕</span>
              Create New Claim
            </button>
            {/* Removed standalone document upload; documents are managed within claims */}
            <button className="action-button" disabled>
              <span className="button-icon">📊</span>
              Generate Report
            </button>
          </div>
          <p className="action-note">Actions will be enabled when claim management features are implemented.</p>
        </div>


      </div>
    </div>
  )
}

export default DashboardPage